// src/lib/state/aiProgress.state.svelte.ts
// Module-level $state — persists across navigation (SPA module never unloads).
// Stores live progress for AI Triage and AI Archive background tasks.

export interface AITaskProgress {
	taskType: 'ai_triage' | 'ai_archive';
	taskId: string;
	stage: string;
	status: 'running' | 'complete' | 'error';
	progress: number; // 0-100
	message: string;
	metadata: Record<string, any>;
	updatedAt: number; // Date.now() for staleness detection
}

// Wrap state in object to avoid direct reassignment of exported $state
export const aiProgressState = $state<{ tasks: Map<string, AITaskProgress> }>({
	tasks: new Map()
});

// Pending auto-clear timeouts (so we can cancel if task restarts)
const clearTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Update or create a task progress entry from a WebSocket TASK_PROGRESS event.
 */
export function updateTaskProgress(data: {
	task_id: string;
	task_type: string;
	stage: string;
	status: string;
	progress: number;
	message: string;
	metadata?: Record<string, any>;
}): void {
	const taskType = data.task_type as AITaskProgress['taskType'];
	if (taskType !== 'ai_triage' && taskType !== 'ai_archive') return;

	// Cancel any pending auto-clear for this task
	const existing = clearTimeouts.get(data.task_id);
	if (existing) {
		clearTimeout(existing);
		clearTimeouts.delete(data.task_id);
	}

	const mappedStatus: AITaskProgress['status'] =
		data.status === 'complete' ? 'complete' : data.status === 'error' ? 'error' : 'running';

	const entry: AITaskProgress = {
		taskType,
		taskId: data.task_id,
		stage: data.stage,
		status: mappedStatus,
		progress: data.progress ?? 0,
		message: data.message ?? '',
		metadata: data.metadata ?? {},
		updatedAt: Date.now()
	};

	// Must create new Map for Svelte reactivity on the Map itself
	const next = new Map(aiProgressState.tasks);
	next.set(data.task_id, entry);
	aiProgressState.tasks = next;

	// Auto-clear completed/error tasks after 10s
	if (mappedStatus === 'complete' || mappedStatus === 'error') {
		const timeout = setTimeout(() => {
			clearTask(data.task_id);
			clearTimeouts.delete(data.task_id);
		}, 10_000);
		clearTimeouts.set(data.task_id, timeout);
	}
}

/**
 * Remove a task from progress tracking.
 */
export function clearTask(taskId: string): void {
	const timeout = clearTimeouts.get(taskId);
	if (timeout) {
		clearTimeout(timeout);
		clearTimeouts.delete(taskId);
	}
	const next = new Map(aiProgressState.tasks);
	next.delete(taskId);
	aiProgressState.tasks = next;
}

/**
 * Get the most recent active task for a given task type.
 */
export function getActiveTask(taskType: 'ai_triage' | 'ai_archive'): AITaskProgress | undefined {
	for (const task of aiProgressState.tasks.values()) {
		if (task.taskType === taskType) return task;
	}
	return undefined;
}

/**
 * Check if there are any tasks currently running (not complete/error).
 */
export function hasActiveTasks(): boolean {
	for (const task of aiProgressState.tasks.values()) {
		if (task.status === 'running') return true;
	}
	return false;
}

/**
 * Check if there are any tasks visible (running, or recently completed/errored).
 */
export function hasVisibleTasks(): boolean {
	return aiProgressState.tasks.size > 0;
}
